import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  FormGroupDirective,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import {ClientConfigurationService, ComponentDeclarationService} from '@twpub/core/services';
import {ALL_COMP_CATEGORIES} from '@twpub/core/enums';
import {ClientConfiguration} from '@twpub/core/utils';
import {finalize} from 'rxjs';
import {NotifierService} from '../../services/notifier.service';
import {NGXLogger} from 'ngx-logger';
import {FormSubmitService} from '../../services/form-submit.service';

import {MatCard, MatCardActions, MatCardContent, MatCardHeader, MatCardTitle} from '@angular/material/card';
import {MatError, MatFormField, MatHint, MatLabel} from '@angular/material/form-field';
import {MatInput} from '@angular/material/input';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import {FontUploadComponent} from '../font-upload/font-upload.component';
import {CssVariablesEditorComponent} from '../css-variables-editor/css-variables-editor.component';
import {ComponentEditorsListComponent} from '../component-editors-list/component-editors-list.component';
import {MatButton} from '@angular/material/button';

@Component({
  selector: 'pub-edit-configuration-form',
  templateUrl: './edit-configuration-form.component.html',
  styleUrls: ['./edit-configuration-form.component.scss'],
  viewProviders: [{provide: ControlContainer, useExisting: FormGroupDirective}],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatCard, MatCardHeader, MatCardTitle, MatCardContent, MatFormField, MatLabel, MatInput, MatError, MatExpansionPanel, MatExpansionPanelHeader, MatExpansionPanelTitle, MatExpansionPanelDescription, MatHint, FontUploadComponent, CssVariablesEditorComponent, MatAccordion, ComponentEditorsListComponent, MatCardActions, MatButton]
})
export class EditConfigurationFormComponent implements OnInit {
  @Input() editConfiguration!: ClientConfiguration;
  @Output() editingDone = new EventEmitter()

  form: UntypedFormGroup = this.fb.group({
    name: ['', [this.existingNameValidator()]],
    css: '',
    fontUrls: '',
    uploadedFontIds: [[]],
    cssFontFamily: ''
  });

  private configs: ClientConfiguration[] = [];

  existingNameValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const name = control.value.trim();
      if (!name) {
        return {empty: true};
      }
      const configByName = this.configService.getByName(this.configs, name);
      if (configByName && configByName.id !== this.editConfiguration?.id) {
        return {nameExists: true}
      }
      return null;
    }
  }

  constructor(private logger: NGXLogger, private fb: UntypedFormBuilder, private compService: ComponentDeclarationService,
              private configService: ClientConfigurationService, private notifier: NotifierService,
              private formSubmitService: FormSubmitService) {
  }

  ngOnInit(): void {
    this.form.setValue({
      name: this.editConfiguration.name || '',
      css: this.editConfiguration.css || '',
      fontUrls: this.editConfiguration.fontUrls ? this.editConfiguration.fontUrls.join('\n') : '',
      uploadedFontIds: this.editConfiguration.fontIds || [],
      cssFontFamily: this.editConfiguration.cssVariables['--tw-font-family'] || ''
    });

    this.configService.getConfigurations().subscribe({
      next: (configs) => {
        this.configs = configs;
      }
    });
  }

  isEditing(): boolean {
    return this.editConfiguration?.id !== 0;
  }

  saveEditConfiguration(close: boolean = false) {
    this.updateEditConfig();
    this.configService.updateConfiguration(this.editConfiguration).pipe(
      finalize(() => {
        const name = this.editConfiguration.name;
        const message = this.isEditing() ? `Configuration ${name} updated` : `Configuration ${name} created`;
        this.notifier.showPopupMessage(message, 'success', true)
        if (close) {
          this.editingDone.emit();
        }
      })
    ).subscribe();
  }

  /**
   * Updates the edit configuration with values from the form.
   */
  private updateEditConfig(): void {
    const f = this.form.value;
    this.editConfiguration.name = f.name.trim();
    this.editConfiguration.css = f.css.trim();
    this.updateEditConfigFonts();
    this.updateComponentConfigs();
    this.editConfiguration.version++;
  }

  private updateEditConfigFonts = () => {
    const f = this.form.value;
    if (typeof f.fontUrls === 'string') {
      this.editConfiguration.fontUrls = f.fontUrls
        .split('\n')
        .map((url: string) => url.trim())
        .filter((url: string) => url.length > 0);
    }
    this.editConfiguration.fontIds = f.uploadedFontIds;
    this.editConfiguration.cssVariables['--tw-font-family'] = f.cssFontFamily.trim();
  }

  /**
   * Updates all component configurations with values from the form.
   */
  updateComponentConfigs() {
    const f = this.form.value;
    const compCategories = ALL_COMP_CATEGORIES;
    const excludedProps = ['componentType']; // Fields set explicity
    compCategories.forEach((compCategory: string) => {
      const formComp = f[compCategory];
      if (formComp) {
        const oldCompConfig = this.editConfiguration.getConfigurationSafe(compCategory);
        const compConfig = this.compService.createComponentConfiguration(formComp.componentType, oldCompConfig?.config);
        if (compConfig) {
          Object.keys(formComp).forEach((key) => {
            if (!excludedProps.includes(key)) {
              compConfig.setConfigValue(key, formComp[key]);
            }
          })
          this.editConfiguration.setConfiguration(compCategory, compConfig);
        }
      }
    });
  }

  cancelEdit() {
    this.editingDone.emit();
  }

  hasError = (ctrlName: string, errName: string) => this.form.get(ctrlName)?.hasError(errName);

  onFormSubmit() {
    this.logger.debug('EditConfigurationFormComponent.onFormSubmit:')
    this.formSubmitService.emitFormSubmit();
  }
}
